import { Building2, Cpu, Wrench, Languages, Brush } from 'lucide-react'
import { useMemo, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { filterSettingsNavItemsForAccess } from './settingsNavItems'
import {
  getSettingsPagePath,
  isV2SettingsShell,
} from './settingsPaths'
import { Separator } from '@/components/ui/separator'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { SidebarNav } from './components/sidebar-nav'

type SettingsNavItemConfig = {
  title: string
  href: string
  icon: JSX.Element
  /** Hidden for view-only users. */
  requiresWriteAccess: boolean
}

export function Settings() {
  const { t } = useTranslation('common')
  const { pathname } = useLocation()
  const canWriteOrgData = useCanWriteOrgData()
  const embedInV2Shell = isV2SettingsShell(pathname)

  const sidebarNavItems = useMemo(() => {
    const allSidebarNavItems: SettingsNavItemConfig[] = [
      {
        title: t('settings.account'),
        href: getSettingsPagePath(pathname, 'account'),
        icon: <Wrench size={18} />,
        requiresWriteAccess: false,
      },
      {
        title: t('settings.organization'),
        href: getSettingsPagePath(pathname, 'organization'),
        icon: <Building2 size={18} />,
        requiresWriteAccess: true,
      },
      {
        title: t('settings.llm'),
        href: getSettingsPagePath(pathname, 'llm'),
        icon: <Cpu size={18} />,
        requiresWriteAccess: true,
      },
      {
        title: t('settings.theme'),
        href: getSettingsPagePath(pathname, 'theme'),
        icon: <Brush size={18} />,
        requiresWriteAccess: false,
      },
      {
        title: t('settings.language'),
        href: getSettingsPagePath(pathname, 'language'),
        icon: <Languages size={18} />,
        requiresWriteAccess: false,
      },
    ]

    return filterSettingsNavItemsForAccess(allSidebarNavItems, canWriteOrgData)
  }, [canWriteOrgData, pathname, t])

  const settingsBody = (
    <>
      <div className='space-y-0.5'>
        <h1 className='text-2xl font-bold tracking-tight md:text-3xl'>
          {t('settings.title')}
        </h1>
        <p className='text-muted-foreground'>{t('settings.description')}</p>
      </div>
      <Separator className='my-4 lg:my-6' />
      <div className='flex flex-1 flex-col space-y-2 overflow-hidden md:space-y-2 lg:flex-row lg:space-y-0 lg:space-x-12'>
        <aside className='top-0 lg:sticky lg:w-1/5'>
          <SidebarNav items={sidebarNavItems} />
        </aside>
        <div className='flex w-full overflow-y-hidden p-1'>
          <Outlet />
        </div>
      </div>
    </>
  )

  if (embedInV2Shell) {
    return <div className="flex flex-col">{settingsBody}</div>
  }

  return (
    <>
      <Header>
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main fixed>{settingsBody}</Main>
    </>
  )
}
